import { useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { ComputeContextKeys } from '../../../hooks/computeContext/const';
import type { createGlobalStateStoreSelectors } from '../selectors';
import type { BaseGlobalStateStoreProviderProps, GlobalStateStore } from '../types';

type Store = ReturnType<typeof createGlobalStateStoreSelectors>;

/**
 * Mirrors `pageParams` from React props into the global state store and pokes
 * the dependency graph so bindings that reference `pageParams` re-evaluate.
 *
 * Why a sync effect rather than reading props directly:
 *   - Bindings (`{{ pageParams.userId }}`) resolve against the store, not props,
 *     so the store must be the source of truth.
 *   - The dependency graph only re-runs dependents when notified — without
 *     `notifyDependencyChange`, a URL like `/users/:userId` change would update
 *     the store but bound expressions would keep evaluating against the stale
 *     value.
 *   - Mirrors `useSyncPageInputs` / `useSyncComponentOverrides`; composed once
 *     in `useSyncGlobalStateProps` so the base provider and every per-instance
 *     wrapper (Module / TemplateComponent) pick it up automatically.
 */
export function useSyncPageParams({
  store,
  pageParams,
  getDependencyGraphActions,
}: {
  // null when the caller is a per-instance wrapper (Module / TC) that has not
  // yet resolved the instance store (page context missing or instance not
  // registered). Effect short-circuits — see useSyncInstanceGlobalState.
  store: Store | null;
  pageParams: BaseGlobalStateStoreProviderProps['pageParams'];
  getDependencyGraphActions: BaseGlobalStateStoreProviderProps['getDependencyGraphActions'];
}) {
  useEffect(() => {
    if (!store) return;
    if (!isEqual(store.getState().pageParams, pageParams)) {
      const nextPageParams: GlobalStateStore['pageParams'] = pageParams ?? {};
      store.setState({ pageParams: nextPageParams });
      getDependencyGraphActions().notifyDependencyChange({ id: ComputeContextKeys.PAGE_PARAMS });
    }
  }, [getDependencyGraphActions, pageParams, store]);
}
